import TableCell from "@mui/material/TableCell";
import TableFooter from "@mui/material/TableFooter";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { SaleDepositTotals } from "../../types/sale-deposit.type";
import { formatNumber, numberWithCommas } from "../../utils";
import DiffAmountText from "./DiffAmountText";
import NetOfNotesHint from "./NetOfNotesHint";
import TransferAmountText from "./TransferAmountText";
import {
  DATE_COLUMN_WIDTH,
  STICKY_BODY_Z_INDEX,
  stickyColumnSx,
} from "./tableLayout";
import {
  TOTALS_ROW_SX,
  TotalsCountCell,
  TotalsMoneyCell,
  TotalsSkeletonFooter,
  totalsCellSx,
} from "./totalsFooter";
import {
  formatCashInHand,
  pendingSlipHint,
  physicalCash,
  transferDiff,
} from "./transfer.utils";

interface FooterSummaryProps {
  totals: SaleDepositTotals | null;
  loading: boolean;
  /** Rows matching the filter — the population the totals were computed over. */
  rowCount: number;
  /** Columns in the table, so this row is exactly as wide as the rows it totals. */
  columnCount: number;
}

/**
 * Cells this row fills with a figure, ending at ผลต่าง. Everything after them carries per-row
 * actions and the two sign-offs — วัน เวลา การโอน, สลิป, ตรวจแล้ว (BO), ตรวจแล้ว (HO),
 * หมายเหตุ, and ขอแก้ไข where the reader may raise one — so the row leaves them blank and
 * counts them off `columnCount` rather than hard-coding a width the table no longer has.
 */
const TOTALLED_COLUMNS = 13;

/**
 * แถวสรุปยอด rendered as a real <TableFooter> inside the same <Table> as the rows, so every
 * figure sits under the column it totals and the row scrolls horizontally with the table.
 *
 * Every figure comes from the API's `totals` object, which the backend aggregates with the
 * SAME where clause as the rows — so it covers the whole filtered result set, not the page on
 * screen. Summing `rows` here would silently report one page.
 *
 * ผลต่าง is derived from the two totals rather than summed per row, and painted by the same
 * DiffAmountText the rows use. Routes that have not submitted a slip contribute 0 to ยอดโอน,
 * so `pending_count` rides along as a tooltip on the ที่ต้องโอน/ยอดโอน cell to keep an
 * outstanding remit from reading as a real shortfall.
 */
export default function FooterSummary({
  totals,
  loading,
  rowCount,
  columnCount,
}: FooterSummaryProps) {
  if (loading) return <TotalsSkeletonFooter columnCount={columnCount} />;

  if (!totals || rowCount === 0) return null;

  // Both operands are always present on the totals object, so the un-submitted case
  // transferDiff guards against cannot arise here.
  const diff =
    transferDiff(totals.required_transfer_amount, totals.transfer_amount) ?? 0;
  return (
    <TableFooter>
      <TableRow sx={TOTALS_ROW_SX}>
        <TableCell
          sx={{ ...stickyColumnSx(0, STICKY_BODY_Z_INDEX), minWidth: DATE_COLUMN_WIDTH }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            รวมทั้งหมด
          </Typography>
          <Typography component="span" fontWeight={700}>
            {numberWithCommas(rowCount)} รายการ
          </Typography>
        </TableCell>
        <TableCell sx={stickyColumnSx(DATE_COLUMN_WIDTH, STICKY_BODY_Z_INDEX)} />
        <TableCell />
        <TotalsCountCell>
          {numberWithCommas(totals.customer_count)}
        </TotalsCountCell>
        <TotalsMoneyCell>{formatNumber(totals.total_amount)}</TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.credit_total_value)}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatCashInHand(physicalCash(totals))}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.transfer_total_value)}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.cash_total_value)}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.deposit_collection_amount)}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.total_expense_amount)}
        </TotalsMoneyCell>
        <TableCell align="right" sx={totalsCellSx}>
          <Tooltip title={pendingSlipHint(totals.pending_count)}>
            <span>
              <TransferAmountText
                required={totals.required_transfer_amount}
                transfer={totals.transfer_amount}
              />
            </span>
          </Tooltip>
          <NetOfNotesHint totalAmount={totals.credit_debit_note_amount} />
        </TableCell>
        <TableCell align="right" sx={totalsCellSx}>
          <DiffAmountText diff={diff} />
        </TableCell>
        {Array.from({ length: columnCount - TOTALLED_COLUMNS }).map(
          (_, index) => (
            <TableCell key={index} />
          ),
        )}
      </TableRow>
    </TableFooter>
  );
}
